export interface ScoreHistoryPoint {
  id: string
  categoryId: string
  categoryName: string
  oldScore: number | null
  newScore: number
  reason: string
  createdAt: string
}

interface ScoreHistoryChartProps {
  points: ScoreHistoryPoint[]
  testId?: string
}

export function ScoreHistoryChart({ points, testId = 'score-history-chart' }: ScoreHistoryChartProps) {
  if (points.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testId}-empty`}>
        No score history yet.
      </p>
    )
  }

  const chronological = [...points].reverse()
  const width = 320
  const height = 120
  const padding = 16
  const innerWidth = width - padding * 2
  const innerHeight = height - padding * 2

  const values = chronological.map((point) => point.newScore)
  const minValue = Math.max(0, Math.min(...values) - 0.05)
  const maxValue = Math.min(1, Math.max(...values) + 0.05)
  const range = Math.max(maxValue - minValue, 0.01)

  const coordinates = values.map((value, index) => {
    const x = padding + (index / Math.max(values.length - 1, 1)) * innerWidth
    const y = padding + innerHeight - ((value - minValue) / range) * innerHeight
    return { x, y, value }
  })

  const polyline = coordinates.map((point) => `${point.x},${point.y}`).join(' ')

  return (
    <div data-testid={testId}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-32 w-full max-w-md rounded-md border border-border bg-muted/20"
        role="img"
        aria-label="Score history chart"
      >
        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-primary"
          points={polyline}
        />
        {coordinates.map((point, index) => (
          <circle
            key={chronological[index]?.id ?? index}
            cx={point.x}
            cy={point.y}
            r="3"
            className="fill-primary"
          />
        ))}
      </svg>

      <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
        {points.slice(0, 5).map((point) => (
          <li key={point.id} data-testid={`score-history-point-${point.id}`}>
            {new Date(point.createdAt).toLocaleString()} · {point.reason} ·{' '}
            {point.newScore.toFixed(2)}
          </li>
        ))}
      </ul>
    </div>
  )
}
